// Local blobs and upload receipts. Scene documents contain stable draft-asset IDs,
// never temporary blob URLs. No upload happens until prepare() is called.
import { frameCore as C } from "./frameCore";
const database = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open("antlogy-post-media", 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore("assets", { keyPath: "key" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
async function transact(mode, action) {
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("assets", mode),
      request = action(tx.objectStore("assets"));
    tx.oncomplete = () => {
      db.close();
      resolve(request?.result);
    };
    tx.onerror = tx.onabort = () => {
      db.close();
      reject(tx.error || new Error("Could not save media on this browser."));
    };
  });
}
export function createDraftStore(uid, postId) {
  const prefix = encodeURIComponent(uid || "anonymous") + "/" + postId + "/";
  const records = new Map(),
    urls = new Map();
  const view = () =>
    Object.fromEntries([...records].map(([ref]) => [ref, urls.get(ref)]));
  function remember(record) {
    records.set(record.ref, record);
    if (!urls.has(record.ref))
      urls.set(record.ref, URL.createObjectURL(record.blob));
  }
  async function write(record) {
    await transact("readwrite", (store) => store.put(record));
    remember(record);
  }
  return {
    async load() {
      const rows = await transact("readonly", (store) => store.getAll());
      rows.filter((row) => row.key.startsWith(prefix)).forEach(remember);
      return view();
    },
    async add(file) {
      const id = C.id(),
        ref = "draft-asset://" + id;
      await write({
        key: prefix + id,
        ref,
        blob: file,
        name: file.name || "image.jpg",
        type: file.type,
        uploaded: null,
      });
      return ref;
    },
    view,
    async htmlForEditor(html) {
      let result = html;
      records.forEach((record, ref) => {
        result = result
          .split(ref)
          .join("https://draft.antlogy.invalid/" + ref.slice(14));
      });
      return result;
    },
    // AntEditor permits HTTPS placeholders. Resolve only our known image IDs after
    // it sanitizes the HTML; never inject unsanitized HTML into its editable area.
    hydrateEditor(root) {
      root
        ?.querySelectorAll('img[src^="https://draft.antlogy.invalid/"]')
        .forEach((img) => {
          const ref =
            "draft-asset://" +
            img
              .getAttribute("src")
              .slice("https://draft.antlogy.invalid/".length);
          if (urls.has(ref)) img.src = urls.get(ref);
        });
    },
    fromEditorHTML(html) {
      urls.forEach((url, ref) => {
        html = html.split(url).join(ref);
      });
      return html.replace(
        /https:\/\/draft\.antlogy\.invalid\/([a-zA-Z0-9_-]+)/g,
        "draft-asset://$1",
      );
    },
    async prepare(frames, upload) {
      const replacements = {};
      for (const ref of C.assetRefs(frames)) {
        const record = records.get(ref);
        if (!record)
          throw new Error(
            "A saved media file is missing. Replace that image or video before publishing.",
          );
        if (!record.uploaded) {
          const file = new File([record.blob], record.name, {
            type: record.type,
          });
          record.uploaded = await upload(file, ref.slice(14));
          if (!C.url(record.uploaded))
            throw new Error("The upload did not return a usable URL.");
          await write(record);
        }
        replacements[ref] = record.uploaded;
      }
      return C.replaceAssets(frames, replacements);
    },
    async clear() {
      await transact("readwrite", (store) => {
        records.forEach((record) => store.delete(record.key));
      });
      records.clear();
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    },
    dispose() {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    },
  };
}
